<template>
  <define-body>
    <pf-empty-state v-if="loading">
      <pf-empty-state-header :title="$t('Caricamento dati di fatturazione...')" heading-level="h4">
        <template #icon>
          <pf-empty-state-icon>
            <pf-spinner />
          </pf-empty-state-icon>
        </template>
      </pf-empty-state-header>
    </pf-empty-state>

    <template v-else>
      <form-group v-if="embedFor === 'approval'" :name="$t('Richiedi accesso come:')" required>
        <template #helper-text v-if="company.approval">
          <pf-content>
            <p v-if="company.approval === 'in approvazione rivenditore'">
              {{ $t('Dopo aver completato la compilazione delle informazioni richieste, procederemo ad attivare il tuo status di partner Artera.') }}
            </p>
            <p v-else-if="company.approval === 'in approvazione cliente'">
              {{
                $t('Una volta che avrai condiviso le tue informazioni, effettueremo una selezione accurata del partner che meglio si adatta alle tue esigenze.')
              }}
            </p>
            <p>
              {{
                $t(
                  "Nessun vincolo contrattuale è applicato; il contratto è completamente privo di restrizioni, inclusi requisiti minimi d'acquisto. Avrai sempre la libertà di decidere come procedere.",
                )
              }}
            </p>
          </pf-content>
        </template>

        <pf-toggle-group v-model="company.approval">
          <pf-toggle-group-item value="in approvazione rivenditore" data-ouia-component-id="approval-form-reseller-access">{{
            $t('Rivenditore')
          }}</pf-toggle-group-item>
          <pf-toggle-group-item value="in approvazione cliente" data-ouia-component-id="approval-form-endcustomer-access">{{
            $t('Cliente Finale')
          }}</pf-toggle-group-item>
        </pf-toggle-group>
        <input type="text" v-model="company.approval" name="approval" required class="input-hidden" />
      </form-group>

      <form-group v-if="!verificato || embedFor === 'prospect'" :name="$t('Tipologia')" required>
        <pf-toggle-group :model-value="company.tipologia" @update:model-value="company.tipologia = $event" :disabled="!!company.locked">
          <pf-toggle-group-item value="Private" data-ouia-component-id="billing-form-tipologa-private">{{ $t('Persona fisica') }}</pf-toggle-group-item>
          <pf-toggle-group-item value="Company" data-ouia-component-id="billing-form-tipologa-company">{{ $t('Azienda') }}</pf-toggle-group-item>
        </pf-toggle-group>
        <input type="text" v-model="company.tipologia" name="tipologia" required class="input-hidden" />
      </form-group>

      <form-group :name="ragioneSocialeLabel" required>
        <x-input
          id="companyRagioneSociale"
          v-model="company.ragione_sociale"
          name="ragione_sociale"
          type="text"
          required
          maxlength="250"
          autocomplete="name"
          :disabled="!!company.locked"
        />
      </form-group>

      <form-group :name="$t('Nazione')" required>
        <pf-form-select
          id="companyNazione"
          :model-value="company.stato"
          @update:model-value="company.stato = String($event)"
          name="stato"
          autocomplete="country"
          :disabled="!!company.locked"
          @change="capChange"
        >
          <pf-form-select-option v-for="country in countries" :key="country.iso" :value="country.iso">
            {{ country.country }}
          </pf-form-select-option>
        </pf-form-select>
      </form-group>

      <pf-grid gutter>
        <pf-grid-item sm="3">
          <form-group :name="$t('CAP')" required>
            <x-input id="companyCAP" v-model="company.cap" name="cap" type="text" required maxlength="10" autocomplete="postal-code" @change="capChange" />
          </form-group>
        </pf-grid-item>

        <pf-grid-item sm="6">
          <form-group :name="$t('Città')" required>
            <x-input
              v-if="autoCities.length < 2"
              id="companyCitta"
              v-model="company.citta"
              name="citta"
              type="text"
              :readonly="autoCities.length == 1"
              required
              maxlength="250"
            />

            <pf-form-select
              v-else
              id="companyCitta"
              :model-value="company.citta"
              @update:model-value="company.citta = $as<(typeof autoCities)[number]>($event)"
              name="citta"
            >
              <pf-form-select-option v-for="citta of autoCities" :key="citta" :value="citta">
                {{ citta }}
              </pf-form-select-option>
            </pf-form-select>
          </form-group>
        </pf-grid-item>

        <pf-grid-item sm="3">
          <form-group :name="company.stato != 'CH' ? $t('Provincia') : $t('Cantone')" required>
            <x-input id="companyProvincia" v-model="company.provincia" name="provincia" type="text" :readonly="autoProvince" required maxlength="2" />
          </form-group>
        </pf-grid-item>
      </pf-grid>

      <form-group :name="personaFisica ? $t('Indirizzo residenza') : $t('Indirizzo sede')" required>
        <x-input id="companyIndirizzo" v-model="company.indirizzo" name="indirizzo" type="text" required maxlength="250" autocomplete="street-address" />
      </form-group>

      <template v-if="company.stato == 'IT' || !personaFisica">
        <form-group
          v-if="!personaFisica"
          :name="$t('Partita IVA')"
          :required="company.stato == 'IT' && !company.codice_fiscale"
          :helper-text="
            company.locked
              ? $t(
                  `Non è possibile modificare la partita IVA. È necessario richiedere il cambio dei dati fatturazione compilando il relativo modulo che si trova nella sezione 'Documenti Utili'. Il modulo andrà poi inviato a finance{'@'}artera.net.`,
                )
              : undefined
          "
        >
          <x-input
            id="companyPiva"
            v-model="company.partita_iva"
            name="partita_iva"
            type="text"
            :required="company.stato == 'IT' && !company.codice_fiscale"
            maxlength="16"
            :disabled="!!company.locked"
          />
        </form-group>

        <form-group v-if="company.stato == 'IT'" :name="$t('Codice fiscale')" :required="!company.partita_iva || personaFisica">
          <x-input
            id="companyCF"
            v-model="company.codice_fiscale"
            name="codice_fiscale"
            type="text"
            :required="!company.partita_iva || personaFisica"
            maxlength="16"
            :disabled="!!company.locked"
          />
        </form-group>
      </template>

      <template v-if="embedFor === null || embedFor === 'prospect'">
        <pf-title h="3" class="pf-v6-u-mt-md">{{ $t('Modalità di fatturazione') }}</pf-title>
        <form-group
          :name="$t('Valuta')"
          required
          :helper-text="
            company.locked || login.currency_locked
              ? $t(
                  `Non è possibile modificare in modo autonomo la valuta. Scrivi a finance{'@'}artera.net per eventuali richieste in merito al cambio delle opzioni di fatturazione.`,
                )
              : undefined
          "
          style="max-width: 300px"
        >
          <pf-form-select
            id="currency"
            :model-value="company.currency"
            name="currency"
            required
            :disabled="embedFor !== 'prospect' && (company.locked || login.currency_locked)"
            @update:model-value="company.currency = $as<Currency>($event)"
          >
            <pf-form-select-option value="EUR">Euro</pf-form-select-option>
            <pf-form-select-option value="CHF">CHF</pf-form-select-option>
          </pf-form-select>
        </form-group>

        <form-group
          :name="$t('Fatturazione unica mensile')"
          :helper-text="
            $t('Attivando questa opzione riceverai al massimo una fattura al mese, alla fine del mese, che comprenderà tutti i servizi rinnovati in quel mese.')
          "
        >
          <pf-switch
            :checked="!!company.fatturazione_mensile"
            @update:checked="company.fatturazione_mensile = Boolean($event)"
            name="fatturazione_mensile"
            :value="true"
            off-value="0"
            :label="company.fatturazione_mensile ? $t('Sì') : $t('No')"
          />
        </form-group>

        <form-group v-if="embedFor === null" :name="$t('Fatture CSV')" :helper-text="$t('Attivando questa opzione riceverai le fatture in formato CSV.')">
          <pf-switch
            :checked="!!company.csv_invoice"
            @update:checked="company.csv_invoice = Boolean($event)"
            name="csv_invoice"
            :value="true"
            off-value="0"
            :label="company.csv_invoice ? $t('Sì') : $t('No')"
          />
        </form-group>

        <pf-title h="3" class="pf-v6-u-mt-md">{{ $t('Preferenze delle Comunicazioni') }}</pf-title>
        <form-group :name="$t('Lingua')" style="max-width: 300px">
          <pf-form-select id="lingua" :model-value="company.lingua" @update:model-value="company.lingua = String($event)" autocomplete="language" name="lingua">
            <pf-form-select-option value="it">
              {{ $t('Italiano') }}
            </pf-form-select-option>
            <pf-form-select-option value="en">
              {{ $t('Inglese') }}
            </pf-form-select-option>
            <pf-form-select-option value="fr">
              {{ $t('Francese') }}
            </pf-form-select-option>
            <pf-form-select-option value="de">
              {{ $t('Tedesco') }}
            </pf-form-select-option>
          </pf-form-select>
        </form-group>
      </template>

      <template v-if="embedFor === null">
        <pf-title h="3" class="pf-v6-u-mt-md">{{ $t('Contatti') }}</pf-title>

        <form-group v-if="company.stato == 'IT'" :name="$t('Posta Elettornica Certificata')">
          <x-input id="companyPEC" v-model="company.email_pec" type="email" maxlength="100" autocomplete="email" />
        </form-group>

        <form-group :name="$t('Telefono')" required style="max-width: 300px">
          <x-input id="companyTelefono" v-model="company.telefono" type="tel" required maxlength="20" autocomplete="tel" />
        </form-group>
      </template>
    </template>
  </define-body>

  <reuse-body v-if="embedFor === 'prospect'" />

  <x-form
    v-else
    v-bind="$attrs"
    data-ouia-component-type="BillingForm"
    :submit-text="$t('Invia')"
    :submit-help="`* ${$t('Campo obbligatorio')}`"
    :disabled="!company.id"
    @submit="submit"
  >
    <reuse-body />
  </x-form>
</template>

<script lang="ts" setup>
import { http } from '../http';
import XForm from './x-form.vue';
import countries from '../countries.json';
import { computed, ref, unref, watch, type Ref } from 'vue';
import { useCompany } from '../utils';
import { useLoginStore } from '@/store/login';
import { useAlertStore } from '@/store/alerts';
import { type Currency, type Company, CompanyResource } from '@/resources';
import axios from 'axios';
import { $t } from '@/i18n';
import { useFunctionRunningState } from '@common/use';
import { createReusableTemplate } from '@vueuse/core';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    embedFor?: 'prospect' | 'order' | 'approval' | null;
  }>(),
  {
    embedFor: null,
  },
);

const emit = defineEmits<{
  (e: 'submit'): void;
}>();

const [DefineBody, ReuseBody] = createReusableTemplate();

const login = useLoginStore();
const alert = useAlertStore();

const { state: asyncCompanyState, result: asyncCompany } = useCompany();

const autoCities: Ref<string[]> = ref([]);
const autoProvince = ref(false);
const company: Ref<{ [P in keyof Company]?: Company[P] | undefined | null }> = ref({
  locked: false,
  currency_locked: false,
  tipologia: 'Company',
  fatturazione_mensile: false,
});

const personaFisica = computed(() => company.value.tipologia == 'Private');

const verificato = computed(() => login.verificato);
const loading = computed(() => sending.value || asyncCompanyState.state !== 'success');
const ragioneSocialeLabel = computed(() => (company.value.tipologia === 'Company' ? $t('Azienda') : $t('Nome e Cognome')));

watch(
  () => asyncCompany.value,
  () => {
    if (props.embedFor !== 'prospect' && asyncCompany.value && company.value.id !== asyncCompany.value.id) {
      company.value = { ...unref(asyncCompany.value) };
    }
  },
);

async function capChange() {
  autoCities.value = [];

  if (!company.value.cap) {
    return;
  }

  try {
    const response = await http.get(`/api/zipcode/${company.value.stato}/${company.value.cap}`, {
      withCredentials: false,
    });

    for (const p of response.data.places) {
      if (!autoCities.value.includes(p['place name'])) {
        autoCities.value.push(p['place name']);
      }
    }
    autoCities.value.sort();
  } catch (e: any) {
    if (!axios.isAxiosError(e) || e.response?.status !== 404) {
      throw e;
    }
  }

  if (autoCities.value.length > 0) {
    const oldCity = company.value.citta;
    if (!oldCity || !autoCities.value.includes(oldCity)) {
      company.value.citta = autoCities.value[0];
    } else {
      company.value.citta = undefined;
      setTimeout(() => {
        company.value.citta = oldCity;
      }, 100);
    }
  }

  if (company.value.stato != 'IT') {
    autoProvince.value = false;
    return;
  }

  try {
    company.value.provincia = (await http.get(`/rest/province/${company.value.cap}`)).data;
    autoProvince.value = true;
  } catch (e) {
    autoProvince.value = false;
  }
}

const { isRunning: sending, run: submit } = useFunctionRunningState(
  async () => {
    if (!company.value.id) {
      return;
    }

    company.value = await new CompanyResource().save(company.value.id, company.value as Partial<Company>);
    alert.add({ title: $t('Profilo aggiornato'), type: 'success', toast: true });
    emit('submit');

    if (company.value.id === login.id_azienda) {
      await verificaProfilo();
    }
  },
  { preventMultiple: true },
);

function verificaProfilo() {
  return login.refresh();
}
</script>
